import { useFeature } from "@growthbook/growthbook-react";
import PropTypes from "prop-types";
import { useMemo } from "react";

import { Button } from "@transfr-inc/dashboard-components/forms";

import { useCareerPlan } from "@features/career-details/api/use-career-plan";
import {
  StartOverConfirmationModal,
  useUserSelectedCareers,
} from "@features/shared";
import {
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Loader,
} from "@features/ui";
import { buttonTracking } from "@lib/tracking.helpers";
import { useUserJourney } from "@pages/journey-map/api/use-user-journey";

import { CareerOptionsList } from "./components";

import "./index.scss";

export const CareerCompare = ({
  onClose,
  onCareerDetailsClick,
  onCreateBudget,
  onViewPathway,
}) => {
  const viewPathwayButtonStyle = useFeature(
    "trek-mobile.view-pathway-button-style",
  );
  const {
    data: { currentJourneyCareer },
  } = useUserJourney();

  const { data: careerPlan } = useCareerPlan();

  const onRemoveCareerToCompare = async (career) => {
    await removeCareer(career);
  };

  const onCareerSelected = async (career) => {
    await selectCareer(career);
  };

  const {
    selectedCareers,
    removeCareer,
    selectCareer,
    removingSelectedCareer,
    fetchingSelectedCareers,
    openStartOverConfirmation,
    onStartOverCancel,
    onStartOverConfirmation,
  } = useUserSelectedCareers();

  const careers = useMemo(
    () =>
      selectedCareers.map((career) => ({
        ...career,
        isSelected: career.id === currentJourneyCareer?.id,
      })),
    [selectedCareers, currentJourneyCareer],
  );

  const toolbar = (
    <>
      <Button
        onClick={onClose}
        icon={["fa-regular", "xmark"]}
        title="close"
        {...buttonTracking("career-compare", "close")}
      />
    </>
  );

  const cardTitle = (
    <h1 className="career-compare-card-title">
      Compare <Badge>{careers?.length ?? "..."}</Badge>
    </h1>
  );

  return (
    <>
      <Loader show={removingSelectedCareer} text="Deleting budget..." />
      <Card className="career-compare-card">
        <CardHeader toolbar={toolbar} title={cardTitle}>
          <div className="subheader">
            Select 1 career to continue your journey.
          </div>
        </CardHeader>
        <CardBody>
          <Loader show={removingSelectedCareer} text="Removing career..." />
          <CareerOptionsList
            careers={careers}
            loading={fetchingSelectedCareers}
            onCareerDetailsClick={onCareerDetailsClick}
            onCareerSelected={onCareerSelected}
            onRemoveCareerToCompare={onRemoveCareerToCompare}
          />
        </CardBody>
        <CardFooter>
          <Button
            onClick={onViewPathway}
            title="View Pathway"
            disabled={!currentJourneyCareer}
            className={viewPathwayButtonStyle.value}
            {...buttonTracking("career-compare", "view-full-pathway")}
          >
            View Full Pathway
          </Button>
          <Button
            onClick={onCreateBudget}
            icon={["fal", "calculator"]}
            title="Create My Budget"
            primary
            disabled={!currentJourneyCareer}
            {...buttonTracking("career-compare", "create-my-budget")}
          >
            Create My Budget
          </Button>
          <p>Create a budget for this career</p>
        </CardFooter>
      </Card>
      <StartOverConfirmationModal
        open={openStartOverConfirmation}
        career={careerPlan?.career}
        onCancel={onStartOverCancel}
        onClose={onStartOverCancel}
        onConfirm={onStartOverConfirmation}
        trackingPrefix="career-compare"
        isNewCareer
      />
    </>
  );
};

CareerCompare.propTypes = {
  onClose: PropTypes.func,
  onCareerDetailsClick: PropTypes.func,
  onCreateBudget: PropTypes.func,
  onViewPathway: PropTypes.func,
};
